/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React from 'react';
import { Button, Typography, makeStyles } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CloseIcon from '@material-ui/icons/Close';
import { useViewAs } from './ViewAsContext';

const useStyles = makeStyles(theme => ({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // The lens accent (same coral as the active "View as" trigger), kept
    // OPAQUE: the coral tint is layered over the page background so the Teams /
    // submenu flyout (which renders just beneath the banner) never shows
    // through the strip. A translucent fill bled the flyout through.
    backgroundColor: theme.palette.background.default,
    backgroundImage:
      theme.palette.type === 'dark'
        ? 'linear-gradient(rgba(255, 110, 91, 0.22), rgba(255, 110, 91, 0.22))'
        : 'linear-gradient(rgba(255, 110, 91, 0.16), rgba(255, 110, 91, 0.16))',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.text.primary,
    padding: theme.spacing(0.75, 2),
    // Sit above the page Header so the mode is unmistakable on every page.
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar + 1,
  },
  icon: { color: theme.palette.primary.main, fontSize: 18, flexShrink: 0 },
  text: { fontSize: '0.8125rem' },
  name: { fontWeight: 600 },
  // The "why" sits quieter than the name so the active subject reads first.
  qualifier: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  spacer: { flex: 1 },
  exit: {
    textTransform: 'none',
    color: theme.palette.text.primary,
    fontSize: '0.8125rem',
  },
}));

/**
 * App-wide banner shown while the View-as lens is active, so the impersonated
 * view is never mistaken for your own. Renders nothing when viewing as
 * yourself. Read-only lens: only m42 surfaces reflect it.
 */
export const ViewAsBanner = () => {
  const classes = useStyles();
  const { viewingAs, viewAsLabel, setViewAs } = useViewAs();

  if (!viewingAs) return null;

  return (
    <div className={classes.bar} role="status">
      <VisibilityIcon className={classes.icon} />
      <Typography className={classes.text}>
        Viewing as <span className={classes.name}>{viewAsLabel}</span>
        <span className={classes.qualifier}>
          {'  '}· read-only lens, M42 surfaces only
        </span>
      </Typography>
      <span className={classes.spacer} />
      <Button
        className={classes.exit}
        size="small"
        startIcon={<CloseIcon fontSize="small" />}
        onClick={() => setViewAs(null)}
      >
        Exit
      </Button>
    </div>
  );
};
